/**
 * Pre-discount cart value for progress bar tiers.
 * Automatic discounts often reduce line_price while items_subtotal_price stays discounted;
 * original_line_price sum and total_price + total_discount both reflect true spend.
 */
function getProgressBarCartTotal(cart) {
    if (!cart) return 0;

    const reconstructed = (Number(cart.total_price) || 0) + (Number(cart.total_discount) || 0);
    let fromOriginalLines = 0;

    if (cart.items?.length) {
        fromOriginalLines = cart.items.reduce(
            (sum, item) => sum + (Number(item.original_line_price) || Number(item.line_price) || 0),
            0
        );
    }

    return Math.max(reconstructed, fromOriginalLines);
}
window.getProgressBarCartTotal = getProgressBarCartTotal;

if (!customElements.get('shipping-bar')) {
    customElements.define('shipping-bar', class ShippingBar extends HTMLElement {
        static get observedAttributes() {
            return ['data-cart-totals'];
        }

        constructor() {
            super();
            this._syncingShippingGift = false;
            this._shippingGiftAddedForUnlock = false;
        }

        connectedCallback() {
            this.update();
        }

        attributeChangedCallback(name, oldValue, newValue) {
            if (name === 'data-cart-totals' && oldValue !== newValue) {
                this.update();
            }
        }

        update() {
            this.calculateAndRender();
        }

        calculateAndRender() {
            const milestones = this.getMilestones();
            if (milestones.length === 0) {
                return;
            }

            const dataRate = parseFloat(this.dataset.amount);
            const amountCalc = !isNaN(dataRate) && dataRate > 0
                ? dataRate
                : Math.min(...milestones.map(m => m.price));
            const cartTotals = parseFloat(this.dataset.cartTotals) / 100;
            if (isNaN(cartTotals)) {
                return;
            }

            const amount = (amountCalc - cartTotals) * 100;
            const moneyFormat = this.dataset.moneyFormat || window.money_format;
            const nextMilestone = milestones.find(m => cartTotals < m.price);
            const allUnlocked = milestones.length > 0 && !nextMilestone;

            const cartContext = this.closest('cart-slide') || this.closest('.main-cart');
            const textContainer = cartContext
                ? cartContext.querySelector('[data-shipping-bar-text]')
                : document.querySelector('[data-shipping-bar-text]');
            const wrapper = this.closest('.shipping-bar-wrapper');

            if (textContainer) {
                // Store original template if not stored
                if (!textContainer.dataset.template) {
                    textContainer.dataset.template = textContainer.innerHTML;
                }

                if (nextMilestone) {
                    this.classList.remove('shipping-unlocked');
                    wrapper?.classList.remove('shipping-unlocked');
                    const remainingCents = Math.max(0, (nextMilestone.price - cartTotals) * 100);
                    const formattedAmount = Shopify.formatMoney(remainingCents, moneyFormat);
                    const template = this.getGoalTemplate(nextMilestone.goalType);
                    textContainer.innerHTML = this.buildMessageFromTemplate(template, formattedAmount, nextMilestone.label);
                    textContainer.style.fontWeight = '400';
                } else if (allUnlocked || amount <= 0) {
                    this.classList.add('shipping-unlocked');
                    wrapper?.classList.add('shipping-unlocked');
                    textContainer.textContent = this.dataset.shippingMessage || '';
                    textContainer.style.fontWeight = '700';
                } else {
                    this.classList.remove('shipping-unlocked');
                    wrapper?.classList.remove('shipping-unlocked');
                    const base = textContainer.dataset.template;
                    textContainer.innerHTML = base.replace(/\|\|amount\|\|/g, Shopify.formatMoney(amount, moneyFormat));
                    textContainer.style.fontWeight = '400';
                }
            }

            this.threshold = amountCalc;
            this.cartTotals = cartTotals;

            this.setBar();
            this.syncShippingBarGift();
        }

        updateCartTotals(cartTotalPrice) {
            if (cartTotalPrice === undefined || cartTotalPrice === null) {
                return;
            }
            this.dataset.cartTotals = String(cartTotalPrice);
        }

        updateFromCart(cart) {
            this.updateCartTotals(getProgressBarCartTotal(cart));
        }

        setBar() {
            const element = this.querySelector('[data-progress-span]');
            if (!element) return;

            if (!this.threshold || this.threshold <= 0) return;

            const marks = this.querySelectorAll('.cart-slide__label');
            const maxThreshold = this.getMaxThreshold(marks);
            if (!maxThreshold || maxThreshold <= 0) return;

            let value = (this.cartTotals / maxThreshold) * 100;
            if (value > 100) value = 100;
            if (value < 0) value = 0;

            element.setAttribute('data-value', value.toFixed(2));
            element.style.setProperty('--bar-width', `${value.toFixed(2)}%`);
            element.textContent = value.toFixed(2);

            this.updateReachedLabels();

            // Position icons
            this.positionIcons();
        }

        updateReachedLabels() {
            const labels = this.querySelectorAll('.cart-slide__label');
            labels.forEach(label => {
                const threshold = parseFloat(label.dataset.price);
                if (isNaN(threshold) || threshold <= 0) {
                    label.classList.remove('is-reached');
                    return;
                }

                label.classList.toggle('is-reached', this.cartTotals >= threshold);
            });
        }

        getMaxThreshold(labels) {
            let maxThreshold = this.threshold;
            labels.forEach(label => {
                const price = parseFloat(label.dataset.price);
                if (!isNaN(price) && price > maxThreshold) {
                    maxThreshold = price;
                }
            });
            return maxThreshold;
        }

        getMilestones() {
            return Array.from(this.querySelectorAll('.cart-slide__label'))
                .map(label => {
                    const price = parseFloat(label.dataset.price);
                    const labelText = label.querySelector('p')?.textContent?.trim() || '';
                    const goalType = (label.dataset.goalType || '').toLowerCase();
                    const order = parseInt(label.dataset.order, 10) || 999;
                    return { price, label: labelText, goalType, order };
                })
                .filter(item => !isNaN(item.price) && item.price > 0)
                .sort((a, b) => a.order - b.order || a.price - b.price);
        }

        getGoalTemplate(goalType) {
            if (goalType === 'discount') return this.dataset.discountTextTemplate;
            if (goalType === 'discount2') return this.dataset.discount2TextTemplate;
            if (goalType === 'gift') return this.dataset.giftTextTemplate;
            return this.dataset.shippingTextTemplate;
        }

        buildMessageFromTemplate(template, formattedAmount, rewardLabel) {
            const safeTemplate = (template || '').trim();
            if (!safeTemplate) {
                return `You’re ${formattedAmount} away from ${rewardLabel || 'next reward'}`;
            }
            return safeTemplate
                .replace(/\|\|amount\|\|/g, formattedAmount)
                .replace(/\|\|label\|\|/g, rewardLabel || '');
        }

        getCartState() {
            return (this.closest('cart-slide') && this.closest('cart-slide').cartState) || window.VanillaCartState || null;
        }

        getShippingGiftStorageKey(variantId) {
            return `shippingBarGiftAdded_${variantId}`;
        }

        getShippingGiftFlag(variantId) {
            try {
                return sessionStorage.getItem(this.getShippingGiftStorageKey(variantId)) === '1';
            } catch (e) {
                return false;
            }
        }

        setShippingGiftFlag(variantId) {
            try {
                sessionStorage.setItem(this.getShippingGiftStorageKey(variantId), '1');
            } catch (e) { }
        }

        clearShippingGiftFlag(variantId) {
            try {
                sessionStorage.removeItem(this.getShippingGiftStorageKey(variantId));
            } catch (e) { }
        }

        isShippingBarGiftLine(item, variantId) {
            if (!item) return false;
            const sameVariant = String(item.variant_id) === String(variantId) || String(item.id) === String(variantId);
            if (!sameVariant) return false;
            const props = item.properties || {};
            return props._shipping_bar_gift === '1';
        }

        async syncShippingBarGift() {
            const threshold = parseFloat(this.dataset.giftThreshold);
            const variantId = (this.dataset.giftVariantId || '').toString().trim();

            if (!variantId || isNaN(threshold) || threshold <= 0) return;
            if (this._syncingShippingGift) return;

            const isUnlocked = this.cartTotals >= threshold;
            if (isUnlocked) {
                await this.addShippingBarGiftOnce(variantId);
            } else {
                await this.removeShippingBarGift(variantId);
            }
        }

        async addShippingBarGiftOnce(variantId) {
            const cartState = this.getCartState();
            if (!cartState || typeof cartState.addItem !== 'function' || typeof cartState.fetchCart !== 'function') return;

            if (this._shippingGiftAddedForUnlock || this.getShippingGiftFlag(variantId)) return;

            this._syncingShippingGift = true;
            try {
                const cart = await cartState.fetchCart();
                const hasGiftAlready = (cart.items || []).some(item => this.isShippingBarGiftLine(item, variantId));
                if (hasGiftAlready) {
                    this._shippingGiftAddedForUnlock = true;
                    this.setShippingGiftFlag(variantId);
                    return;
                }

                await cartState.addItem(variantId, 1, { _shipping_bar_gift: '1' });
                this._shippingGiftAddedForUnlock = true;
                this.setShippingGiftFlag(variantId);
            } catch (error) {
                this._shippingGiftAddedForUnlock = false;
                this.clearShippingGiftFlag(variantId);
                console.error('Shipping bar gift auto-add failed:', error);
            } finally {
                this._syncingShippingGift = false;
            }
        }

        async removeShippingBarGift(variantId) {
            const cartState = this.getCartState();
            if (!cartState || typeof cartState.updateItemByLine !== 'function' || typeof cartState.fetchCart !== 'function') return;

            this._syncingShippingGift = true;
            try {
                const cart = await cartState.fetchCart();
                const linesToRemove = (cart.items || [])
                    .map((item, index) => ({ item, line: index + 1 }))
                    .filter(({ item }) => this.isShippingBarGiftLine(item, variantId));

                for (const { line } of linesToRemove) {
                    await cartState.updateItemByLine(line, 0);
                }
            } catch (error) {
                console.error('Shipping bar gift auto-remove failed:', error);
            } finally {
                this._shippingGiftAddedForUnlock = false;
                this.clearShippingGiftFlag(variantId);
                this._syncingShippingGift = false;
            }
        }

        // Position icons using display order (left to right on the bar)
        positionIcons() {
            const marks = Array.from(this.querySelectorAll('.cart-slide__label'))
                .sort((a, b) => (parseInt(a.dataset.order, 10) || 999) - (parseInt(b.dataset.order, 10) || 999));
            if (marks.length === 0) return;

            marks.forEach((mark, index) => {
                const count = marks.length;
                const percentPosition = count === 1
                    ? 50
                    : ((index + 1) / count) * 100;
                const clamped = Math.min(92, Math.max(8, percentPosition));

                mark.style.position = 'absolute';
                mark.style.left = `${clamped}%`;
                mark.style.top = '0px';
                mark.style.transform = 'translateX(-50%)';

                if (index === count - 1) {
                    mark.style.left = '100%';
                    mark.style.transform = 'translateX(-100%)';
                }
            });
        }
    });
}

document.addEventListener('cart:updated', (e) => {
    const cart = e.detail;
    if (!cart) return;
    document.querySelectorAll('shipping-bar').forEach((bar) => {
        if (typeof bar.updateFromCart === 'function') {
            bar.updateFromCart(cart);
        }
    });
});
